import React, { useState, useContext, useEffect, useImperativeHandle } from "react";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import { nanoid } from "nanoid";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";
import api from "services/api";
import { filterParser } from "utils/helper";

import DropZone from "components/UpStyled/DropZone";
import StyleCard2 from "components/UpStyled/StyleCard2";
import EditRecordModal from "pages/de/project/create-project/components/EditRecordModal";

const ImportStyleSingleModal = ({ visible, handleClose, handleOk }, ref) => {
  const [selectedStyle, setSelectedStyle] = useState({});
  const [styleList, setStyleList] = useState({ hits: [], nbHits: 0, estimatedTotalHits: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchStr, setSearchStr] = useState("");
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [editRecordVisible, setEditRecordVisible] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    if (visible) {
      const offset = pageSize * (page - 1);
      retrieveStyles(offset, pageSize, "");
      retrieveCollections();
    }
  }, [visible]);

  const retrieveStyles = async (offset = 0, limit = pageSize, q = "", filter = {}) => {
    const payload = { offset, limit };
    if (q) {
      payload["q"] = q;
    }
    let myFilter = { status: "design", binding: { neq: "ongoing" } };
    if (Object.keys(filter).length > 0) {
      myFilter = { ...myFilter, ...filter };
    }
    const newFilterStr = filterParser(myFilter);
    if (newFilterStr) {
      payload["filter"] = newFilterStr;
    }
    try {
      const { data } = await api.products.searchStyles(payload);
      setStyleList(data ? data : { hits: [], nbHits: 0, estimatedTotalHits: 0 });
    } catch (err) {
      console.log(err);
    }
  };

  const retrieveCollections = async () => {
    try {
      const { data } = await api.collections.list();
      const options = data.collections.map((x) => ({ label: x.title, value: x.id }));
      setCollectionOptions(options || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleApply = (data) => {
    const offset = pageSize * (page - 1);
    const limit = pageSize;
    setSearchStr(data["keywords"]);

    let myFilter = { status: "design" };
    if (data["collection_id"]) {
      myFilter["collection_id"] = data["collection_id"]?.value;
    }

    retrieveStyles(offset, limit, data["keywords"], myFilter);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const offset = pageSize * (newPage - 1);
    const limit = pageSize;
    retrieveStyles(offset, limit, searchStr);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    const offset = 0;
    const limit = newPageSize;
    retrieveStyles(offset, limit, searchStr);
  };

  const totalNumber = styleList.nbHits ? styleList.nbHits : styleList.estimatedTotalHits;
  const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

  const handleSelectStyle = (p) => {
    setSelectedStyle(p);
  };

  const handleInputExtraInfo = (e, p) => {
    if (selectedStyle?.id || p?.id) {
      if (p?.id) {
        setSelectedStyle(p);
      }
      setEditRecordVisible(true);
    } else {
      toast.error("Please select a style!");
    }
  };

  const handleEditRecordMeta = (data) => {
    let newStyles = {
      ...selectedStyle,
      style: {
        ...selectedStyle.style,
        quantity: data["quantity"],
        cost_per_unit: data["cost_per_unit"],
      },
    };

    handleOk(newStyles);
    setEditRecordVisible(false);
    setSelectedStyle({});
  };

  return (
    <SuiBox>
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xl"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 0 }}>
          Import your styles{" "}
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 1 }}>
            The selected style will be <strong>duplicated</strong> to the project.
          </DialogContentText>
          <SuiBox component="form" pb={1} onSubmit={handleSubmit(handleApply)}>
            <SuiBox display="flex" alignItems="center" sx={{ gap: 1.5, flexWrap: "wrap" }}>
              <SuiTypography variant="button" color="dark" fontWeight="regular">
                Filter styles by:
              </SuiTypography>
              <div>
                <Controller
                  name="collection_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <SuiBox>
                        <SuiSelect
                          {...field}
                          placeholder="Collection"
                          isSearchable={true}
                          options={collectionOptions}
                          isClearable
                          escapeClearsValue
                        />{" "}
                      </SuiBox>
                    );
                  }}
                />
              </div>
              <div>
                <SuiInput placeholder="Keywords" type="text" {...register("keywords")} />
              </div>
              <div>
                <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                  Apply
                </SuiButton>
              </div>
            </SuiBox>
          </SuiBox>
          <SuiBox>
            <SuiBox
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              gap={1}
              sx={{ flexWrap: "wrap" }}
            >
              {styleList?.hits.map((p, inx) => (
                <SuiBox
                  key={inx}
                  sx={{
                    cursor: "pointer",
                    border: `${selectedStyle.id == p.id ? "2px" : "0px"} solid`,
                    borderColor: ({ palette }) => palette?.secondary?.focus,
                  }}
                  onClick={(e) => handleSelectStyle(p)}
                  // onDoubleClick={(e) => handleInputExtraInfo(e, p)}
                >
                  <StyleCard2 product={p} size="md" detailUrl={""}></StyleCard2>
                </SuiBox>
              ))}
            </SuiBox>
          </SuiBox>
          <SuiBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            px={3}
          >
            <SuiBox display="flex" justifyContent="flex-start" alignItems="center">
              <SuiBox mb={{ xs: 3, sm: 0 }}>
                <SuiTypography variant="button" color="secondary" fontWeight="regular">
                  Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
                </SuiTypography>
              </SuiBox>

              <SuiBox display="flex" alignItems="center" gap={1}>
                <SuiSelect
                  defaultValue={{ value: pageSize, label: pageSize }}
                  options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                  onChange={handleChangeRowsPerPage}
                  size="small"
                />
                <SuiTypography variant="caption" color="secondary">
                  entries per page
                </SuiTypography>
              </SuiBox>
            </SuiBox>
            <Pagination
              variant={"outlined"}
              color={"info"}
              count={pageOptions.length}
              defaultPage={page}
              onChange={handleChangePage}
            />
          </SuiBox>
        </DialogContent>
        <DialogActions>
          <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
            Cancel
          </SuiButton>
          <SuiButton
            size="medium"
            variant="gradient"
            color="dark"
            onClick={() => handleOk(selectedStyle)}
          >
            Import
          </SuiButton>
        </DialogActions>
      </Dialog>
      {editRecordVisible && selectedStyle?.title ? (
        <EditRecordModal
          handleClose={() => {
            setEditRecordVisible(false);
          }}
          editType={"all"}
          handleOk={(data) => handleEditRecordMeta(data)}
          visible={editRecordVisible}
          style={selectedStyle}
        />
      ) : null}
    </SuiBox>
  );
};

export default ImportStyleSingleModal;
